<template>
  <BaseDropdownWrapper :off="props.readonly" class="base-select" data-testid="select">
    <template #control>
      <BaseInput
        :model-value="value"
        :placeholder="props.placeholder"
        :variant="props.variant"
        :size="props.size"
        :has-append-icon="props.hasAppendIcon"
        :has-prepend-icon="props.hasPrependIcon"
        :with-clear="props.withClear"
        :with-shadow="props.withShadow"
        with-full
        readonly
        :with-readonly-icon="false"
        @cleared="handleClear"
      >
        <template v-if="props.hasPrependIcon || props.prependIcon" #prepend-icon>
          <slot name="prepend-icon">
            <BaseIcon
              v-if="props.prependIcon"
              :name="props.prependIcon"
              color="var(--color-foreground-primary)"
              size="20px"
            />
          </slot>
        </template>
        <template
          v-if="(props.hasAppendIcon && !props.readonly) || props.appendIcon"
          #append-icon
        >
          <slot name="append-icon">
            <BaseIcon
              :name="props.appendIcon"
              :color="props.appendIconColor"
              size="20px"
              class="base-select__icon"
              clickable
              @click="emit('clickAppendIcon', props.name)"
            />
          </slot>
        </template>
      </BaseInput>
    </template>
    <template #dropdown="{ handleClose }">
      <button
        v-for="(item, index) in props.options"
        :key="index"
        class="base-select__option"
        :class="{
          ['base-select__option--active']: item.value === props.modelValue,
        }"
        @click="handleSelectOption(item.value, handleClose)"
      >
        {{ item.label }}
      </button>
    </template>
  </BaseDropdownWrapper>
</template>

<script setup lang="ts">
import BaseDropdownWrapper from '../dropdown-wrapper/index.vue'
import BaseIcon from '../icon/index.vue'
import BaseInput from '../input/index.vue'

export interface OptionType {
  label: string
  value: string
}

interface Props {
  modelValue: OptionType['value']
  name?: string
  options: OptionType[]
  placeholder?: string
  variant?: 'light'
  readonly?: boolean
  size?: 'md' | 'md-alt' | 'lg'
  hasPrependIcon?: boolean
  hasAppendIcon?: boolean
  prependIcon?: string
  appendIcon?: string
  appendIconColor?: string
  appendLabel?: string | number
  withClear?: boolean
  alwaysShowClear?: boolean
  withShadow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  hasAppendIcon: true,
  appendIcon: 'vuesax:arrow-down',
  appendIconColor: 'var(--color-foreground-primary)',
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: OptionType['value']): void
  (e: 'cleared', name?: string): void
  (e: 'clickAppendIcon', name?: string): void
}>()

const selected = computed(
  () =>
    props.options?.find(item => item.value === props.modelValue)?.label ?? '',
)

const value = computed(() => {
  let result = selected.value

  if (props.appendLabel) {
    result = `${result} ${props.appendLabel}`
  }

  return result
})

function handleSelectOption(
  value: OptionType['value'],
  handleClose: () => void,
) {
  emit('update:modelValue', value)
  handleClose()
}

function handleClear() {
  emit('update:modelValue', '')
  emit('cleared', props.name)
}
</script>

<style lang="scss" scoped>
.base-select {
  display: inline-flex;

  &__icon {
    transition: all 0.3s ease;
  }

  &__option {
    @include drop-button-styles;

    font-size: rem(14px);
    line-height: rem(14px);
    font-weight: 400;
    width: 100%;
    padding: rem(9px) rem(7px);
    text-align: left;
    background: transparent;
    transition: all 0.25s ease;
    color: var(--color-foreground-regular);
    border-radius: 5px;
    box-shadow: 0 0 0 0 rgb(0 0 0 / 5%);
    margin: 2px 0;
    display: flex;
    justify-content: flex-start;
    overflow: hidden;
    max-height: 40px;

    &:hover:not(.base-select__option--active) {
      transform: translateX(rem(4px));
      font-weight: 500;
      color: var(--color-foreground-primary);
    }

    &--active {
      background-color: rgb(26 92 255 / 5%);
      color: var(--color-foreground-primary);
    }
  }

  &:deep(input) {
    font-weight: 500;
  }

  &--is-open {
    .base-select__icon {
      transform: rotate(180deg);
    }
  }
}
</style>
